<template>
  <div v-if="!category" class="flex-1 flex flex-col items-center justify-center text-gray-400 py-10">
    <span class="material-symbols-outlined text-6xl mb-4 text-primary/40">smart_toy</span>
    <p class="text-lg font-medium text-gray-500">Sol panelden bir kategori seçin</p>
    <p class="text-sm mt-2 text-gray-400">Her kategori için ayrı prompt ayarları yapabilirsiniz</p>
  </div>

  <div v-else class="flex-1 flex flex-col overflow-y-auto">
    <div class="border-b pb-4 mb-6">
      <div class="flex items-center justify-between">
        <h3 class="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <span class="material-symbols-outlined text-primary">smart_toy</span> {{ category.name }} </h3>
        <VButton v-if="windowWidth < 768" @click="$emit('back')" button-text="Geri" icon="arrow_back" :show-icon="true" variant="secondary" size="sm" class="md:hidden" title="Kategorilere Dön"/>
      </div>
      <p class="text-sm text-gray-500">Kategori seviyesi: {{ category.level }}</p>
    </div>

    <div v-if="isLoading" class="flex-1 flex justify-center items-center py-10">
      <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
    </div>

    <div v-else class="space-y-6 flex-1 overflow-y-auto thin-scrollbar">
      <!-- Bilgilendirme Mesajı - Prompt Yok -->
      <div v-if="!imagePrompt && !videoPrompt" class="p-4 bg-orange-50 rounded-lg border border-orange-200 text-orange-800 mb-6">
        <div class="flex items-center gap-2 mb-2">
          <span class="material-symbols-outlined">info</span>
          <h3 class="font-medium">Henüz prompt kaydedilmemiş</h3>
        </div>
        <p class="text-sm">Bu kategori için henüz bir prompt kaydedilmemiş. Aşağıdaki alanlara prompt bilgilerini girerek kaydedebilirsiniz.</p>
      </div>

      <!-- Image Prompt -->
      <div class="mb-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <label class="text-sm font-medium text-gray-800 mb-2 flex items-center gap-2"> <span class="material-symbols-outlined text-primary">image</span> Görsel Prompt </label>
        <p class="text-sm text-gray-600 mb-3">
          Bu kategori için oluşturulacak görsellerin nasıl olması gerektiğini tanımlayın </p>
        <textarea v-model="imagePrompt" class="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors" placeholder="Bu kategori için görsel oluşturulurken AI'ın nasıl bir görsel oluşturması gerektiğini açıklayın..."></textarea>
      </div>

      <!-- Video Prompt -->
      <div class="mb-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <label class=" text-sm font-medium text-gray-800 mb-2 flex items-center gap-2"> <span class="material-symbols-outlined text-primary">movie</span> Video Prompt </label>
        <p class="text-sm text-gray-600 mb-3">
          Bu kategori için oluşturulacak videoların nasıl olması gerektiğini tanımlayın </p>
        <textarea v-model="videoPrompt" class="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors" placeholder="Bu kategori için video oluşturulurken AI'ın nasıl bir video oluşturması gerektiğini açıklayın..."></textarea>
      </div>

      <!-- Quick Templates -->
      <div class="mb-8 bg-blue-50 p-5 rounded-lg border border-blue-200">
        <h4 class="text-sm font-medium text-blue-700 mb-3 flex items-center gap-2">
          <span class="material-symbols-outlined">bolt</span> Hızlı Şablonlar </h4>
        <div class="flex flex-wrap gap-2">
          <button v-for="template in promptTemplates" :key="template.id" @click="applyTemplate(template)" class="px-3 py-1.5 bg-white border border-gray-300 text-sm rounded-full hover:bg-primary hover:text-white hover:border-primary transition-all">
            {{ template.name }}
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-8 sticky bottom-0 bg-white py-4 border-t">
        <div class="flex items-center gap-3">
          <VButton @click="resetPrompts" button-text="Temizle" icon="refresh" :show-icon="true" variant="secondary"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'
import VButton from '@/components/base/buttons/VButton.vue'
import { fetchPrompt, trackPromptChanges } from '../../services/prompt'
import { useAssetStore } from '@/stores/asset'

const props = defineProps({
  category: {
    type: Object,
    default: null,
  },
  windowWidth: {
    type: Number,
    default: window.innerWidth,
  },
  changedCategories: {
    type: Set,
    required: true,
  },
})

const emit = defineEmits(['update:prompt', 'back'])

const assetStore = useAssetStore()
const isLoading = ref(false)
const imagePrompt = ref('')
const videoPrompt = ref('')

// Örnek prompt şablonları
const promptTemplates = [
  { id: 1, name: 'Profesyonel Fotoğraf', imagePrompt: 'Profesyonel, yüksek çözünürlüklü, net aydınlatma, DSLR kamera ile çekilmiş, stüdyo kalitesinde' },
  { id: 2, name: 'Minimalist Stil', imagePrompt: 'Minimalist, sade, temiz arka plan, az renk, boş alan, modern, şık' },
  { id: 3, name: 'Eğitim Videosu', videoPrompt: 'Eğitim amaçlı, adım adım açıklamalı, anlaşılır, görsel örnekli' },
  { id: 4, name: 'Ürün Tanıtımı', videoPrompt: 'Ürün özellikleri, yakın çekim detaylar, kullanım örneği, beyaz arka fon' },
]

// Kategori değiştiğinde promptları yükle
watch(() => props.category, async (newCategory) => {
  if (newCategory) {
    await fetchCategoryPrompt()
  } else {
    resetPrompts()
  }
}, { immediate: true })

// Kategori prompt bilgilerini getir
const fetchCategoryPrompt = async () => {
  if (!props.category) {
    resetPrompts()
    return
  }

  isLoading.value = true

  try {
    const promptData = await fetchPrompt(props.category, assetStore.assetId)

    imagePrompt.value = promptData.imagePrompt
    videoPrompt.value = promptData.videoPrompt
  } catch (error) {
    console.error('Error fetching prompt:', error)
    resetPrompts()
  } finally {
    isLoading.value = false
  }
}

// Değişiklikleri izle
watch([imagePrompt, videoPrompt], ([newImagePrompt, newVideoPrompt]) => {
  if (!props.category) return

  // Değişiklik kontrolü ve kaydedilecek kategorileri işaretle
  const hasChanges = trackPromptChanges(
      props.category,
      newImagePrompt,
      newVideoPrompt,
      props.changedCategories,
  )

  // Üst bileşene bildir
  emit('update:prompt', {
    categoryId: props.category.id,
    hasChanges,
    imagePrompt: newImagePrompt,
    videoPrompt: newVideoPrompt,
  })
})

// Prompt şablonu uygula
const applyTemplate = (template) => {
  if (template.imagePrompt) {
    imagePrompt.value = template.imagePrompt
  }
  if (template.videoPrompt) {
    videoPrompt.value = template.videoPrompt
  }
}

// Promptları sıfırla
const resetPrompts = () => {
  if (props.category?.prompt) {
    imagePrompt.value = props.category.prompt.image_prompt || ''
    videoPrompt.value = props.category.prompt.video_prompt || ''

    // Geçici değerleri temizle
    delete props.category.prompt.tempImagePrompt
    delete props.category.prompt.tempVideoPrompt
    props.category.hasPromptChanges = false
    props.changedCategories.delete(props.category.id)
  } else {
    imagePrompt.value = ''
    videoPrompt.value = ''
  }
}
</script>